import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { loadDecisionSummary, loadContractGenPermissions, generateContract } from '../actionCreators';
import { getDecisionSummary, getContractGenPermissions } from '../selectors';
import DecisionSummaryCard from './DecisionSummaryCard';
import withCustomComponent from 'providers/withCustomComponent';
import { CALCULATE_APPROVAL_MUTATION } from '../../../../../folder/edit/generalData/info/mutations';
import { useMutation } from '@apollo/client';
import { showError } from 'actions/actionCreators/snackbarActions';

const DecisionSummaryContainer = ({ documentId, siteId, decisionSummary, actions, contractGenPermissions }) => {
  const [localContractGenPermissions, setLocalContractGenPermissions] = useState(contractGenPermissions);

  useEffect(() => {
    actions.loadDecisionSummary(documentId, siteId);
    actions.loadContractGenPermissions(documentId, siteId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    actions.loadDecisionSummary(documentId, siteId);
  }, [documentId, siteId, decisionSummary.loaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (contractGenPermissions.loaded) {
      let genPermissions = { ...contractGenPermissions, generating: false };
      setLocalContractGenPermissions(genPermissions);
    } else {
      actions.loadContractGenPermissions(documentId, siteId);
    }
  }, [documentId, siteId, contractGenPermissions.loaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const genContract = useCallback(async () => {
    let genPermissions = { ...contractGenPermissions, generating: true };
    setLocalContractGenPermissions(genPermissions);

    await actions.generateContract(documentId, siteId);

    genPermissions = { ...contractGenPermissions, generating: false };
    setLocalContractGenPermissions(genPermissions);
  }, [actions, contractGenPermissions, documentId, siteId]);

  const [calculateApproval] = useMutation(CALCULATE_APPROVAL_MUTATION, {
    onError: error => {
      showError(error.message);
    }
  });

  const onCalculateApproval = useCallback(() => {
    calculateApproval({
      variables: {
        input: {
          documentId,
          siteId
        }
      }
    });
  }, [calculateApproval, documentId, siteId]);

  return (
    <DecisionSummaryCard
      decisionSummary={decisionSummary}
      contractGenPermissions={localContractGenPermissions}
      generateContract={genContract}
      onCalculateApproval={onCalculateApproval}
    />
  );
};

DecisionSummaryContainer.propTypes = {
  actions: PropTypes.object.isRequired,
  documentId: PropTypes.number,
  siteId: PropTypes.number,
  decisionSummary: PropTypes.object.isRequired,
  contractGenPermissions: PropTypes.object.isRequired
};

function mapStateToProps(state, route) {
  const key = `${route.documentId}-${route.siteId}`;
  return {
    decisionSummary: getDecisionSummary(state, key),
    contractGenPermissions: getContractGenPermissions(state, key)
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadDecisionSummary,
        loadContractGenPermissions,
        generateContract
      },
      dispatch
    )
  };
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withCustomComponent('DecisionSummaryContainer')
)(DecisionSummaryContainer);
